import { z } from 'zod';
import { AttachmentSchema, MAX_MESSAGES, MessageSchema, Rfc3339DateTime } from './create-chat.js';

/**
 * Shared, **versioned** wire contract for the `ui` BFF → `workstreamagent`
 * (Python) cross-module invoke boundary (D4-14 / ADR-0029 / ADR-0030).
 *
 * The BFF invokes the Python function's `live` alias synchronously
 * (`RequestResponse`) and validates the reply against {@link AgentInvokeResponseSchema}.
 * One source of truth both sides reason about — the BFF imports it directly; the
 * Python handler's echo shape is the same contract transcribed in Python.
 *
 * **Expand-contract (ADR-0029).** {@link AGENT_CONTRACT_VERSION} pins the request
 * shape; bump it only on a breaking change (additive fields don't bump it). The
 * **response** is **tolerant-out** (`z.looseObject`) so the agent module can add
 * fields and roll forward/back independently of the BFF — a new agent field never
 * breaks an old BFF, mirroring the ADR-0024 §11 response posture.
 *
 * NB (D4-13 reconciliation): the deployed Python function is a minimal structured
 * **echo** (no model/inference yet), so the response carries `echo` rather than a
 * score. When the real agentic handler lands, it adds fields under the same
 * tolerant-out response — no BFF change required, no version bump.
 */

/**
 * The BFF→Python invoke-contract version. A breaking change to
 * {@link AgentInvokeRequestSchema} bumps this; additive optional fields do not.
 */
export const AGENT_CONTRACT_VERSION = 1;

/** Generous cap on the free-text probe message — bounds the invoke payload (OWASP API4). */
const MAX_MESSAGE_LEN = 10_000;

/**
 * A deliverable an agent wrote this turn (ADR-0040) — the typed mirror of the
 * Python `ArtifactRecord`. `key` is the durable identity (the ADR-0039 S3
 * layout); `url` is a presigned GET — turn-scoped best-effort (it dies with
 * the agent Lambda's role session no matter the requested expiry); `preview`
 * is a capped text head (8k chars, text-family content types only) for
 * inline rendering without any browser→S3 fetch.
 */
export const TurnArtifactSchema = z.strictObject({
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  sizeBytes: z.number().int().nonnegative(),
  key: z.string().min(1),
  // Python serialises absent as null — accept both, normalise to undefined.
  url: z
    .url()
    .nullish()
    .transform((v) => v ?? undefined),
  preview: z
    .string()
    .nullish()
    .transform((v) => v ?? undefined),
});
export type TurnArtifact = z.infer<typeof TurnArtifactSchema>;

/**
 * The payload the BFF sends as the Lambda invoke event. Strict — the BFF authors
 * it, so an unexpected key is a bug. `requestId` doubles as the **idempotency /
 * correlation key** carried into the callee (the agent honours it once it becomes
 * stateful; synchronous `RequestResponse` is not auto-retried, so this is the
 * only dedup seam — D4-14 sync-client policy).
 */
export const AgentInvokeRequestSchema = z.strictObject({
  contractVersion: z.literal(AGENT_CONTRACT_VERSION),
  requestId: z.string().min(1),
  occurredAt: Rfc3339DateTime,
  message: z.string().min(1).max(MAX_MESSAGE_LEN),
  // Librarian extensions — additive, optional. Old callers omit them safely; no version bump.
  workstreamId: z.string().min(1).optional(),
  // `exitDiscovery` transitions a workstream from discovery mode to normal routing.
  // `pollTurn` reads an async turn's status/result (ADR-0037) — a cheap sync invoke.
  // `listArtifacts` / `getArtifact` / `deleteArtifact` are the Drop-6 Files API
  // (ADR-0045) — the BFF's artifact routes invoke these; the ownership check is
  // at the BFF, so the agent stays tenant-agnostic. `listMessages` is the Drop-7
  // transcript read path (ADR-0047) — reads `WorkstreamState.recent_messages`.
  // `deleteWorkstream` hard-deletes a whole workstream — the agent removes its
  // DynamoDB state item (chat + ledger) and every S3 artifact in the ledger; the
  // BFF is the sole ownership gate and deletes its own row AFTER the agent (so a
  // failed agent delete leaves the workstream intact + retryable, never orphaned).
  // `beginUpload` / `finalizeUpload` are the Drop 8 Files-rail upload flow
  // (Phase D/E build the handlers; registered here first so both land under one
  // additive-optional contract, no version bump). `beginUpload` signs a
  // presigned POST policy (no state I/O); `finalizeUpload` verifies the S3
  // object, untags it, and appends the ledger entry.
  action: z
    .enum([
      'chat',
      'create',
      'ping',
      'exitDiscovery',
      'pollTurn',
      'listArtifacts',
      'getArtifact',
      'deleteArtifact',
      'deleteWorkstream',
      'listMessages',
      'beginUpload',
      'finalizeUpload',
      'stageAttachment',
      'listMemory',
      'acceptMemory',
      'deleteMemory',
    ])
    .optional(),
  // ADR-0046: the artifact to fetch/delete on `getArtifact` / `deleteArtifact` —
  // an OPAQUE per-artifact id (the uuid segment of the S3 key). The agent resolves
  // it to the durable key via THIS workstream's ledger, so it is inherently scoped
  // (an id absent from the ledger → not found) — no client-supplied key, no prefix
  // string-confinement, no `%2F`-in-path fragility (supersedes ADR-0045's
  // key-as-artifactId). Absent for `listArtifacts` (the whole workstream).
  artifactId: z.string().min(1).max(128).optional(),
  /**
   * ADR-0056: the memory entry to accept/forget on `acceptMemory` /
   * `deleteMemory` — an OPAQUE per-entry id resolved against THIS workstream's
   * memory list, so it is inherently scoped (an id absent from the list simply
   * does not resolve), exactly as `artifactId` is against the ledger. Absent
   * for `listMemory` (the whole workstream). No memory write carries text: the
   * user accepts or forgets what an agent proposed, never authors an entry.
   */
  memoryId: z.string().min(1).max(128).optional(),
  /**
   * ADR-0037: true on an `Event` (async) invoke — the agent claims + persists
   * the turn (`running` → `complete`/`failed`) instead of replying inline.
   */
  async: z.boolean().optional(),
  /** ADR-0037: the turn to read on `action: 'pollTurn'` (= the submit's requestId). */
  turnId: z.string().min(1).optional(),
  agentName: z.string().optional(),
  /**
   * ADR-0080 §6: the caller's org from verified claims — the agent forwards it to
   * agentforge's `resolveMembers` and nothing else. Mirrors the Python pattern.
   */
  orgId: z
    .string()
    .regex(/^org_[A-Za-z0-9]{1,64}$/)
    .optional(),
  /**
   * AB-2-1: the caller's sub from verified claims — the agent forwards it to
   * agentforge's `runAgent` so a draft Forge saves has a human owner.
   */
  userSub: z.string().min(1).max(256).optional(),
  forcedAgents: z.array(z.string()).optional(),
  /**
   * ADR-0047: newest-N bound for `action: 'listMessages'` — the agent returns at
   * most this many of the newest messages (chronological order). The store caps
   * at 40 today; 100 matches the wire's MAX_MESSAGES ceiling.
   */
  limit: z.number().int().min(1).max(MAX_MESSAGES).optional(),
  // Full transcript for Router context — mirrors the Python contract's
  // `messages: list[Message] | None` (contracts/invoke.py). Missed in the
  // original port; added here to match.
  messages: z.array(MessageSchema).max(MAX_MESSAGES).optional(),
  // PDF attached to THIS turn only (additive-optional; mirrored in invoke.py).
  attachment: AttachmentSchema.optional(),
  /**
   * ADR-0075: on an async submit whose PDF was parked first by a sync
   * `stageAttachment` invoke (the Event payload caps at 1 MB). The agent derives
   * the S3 key from `workstreamId` + `requestId` — no key rides the wire.
   */
  stagedAttachmentName: z.string().min(1).max(255).optional(),
  /**
   * Drop 8 upload flow (ADR-0049). `filename` on `beginUpload` (the source for
   * the signed key + policy) and `finalizeUpload` (the commit body — the SPA
   * declares no `contentType`/`sizeBytes`, those come from S3's own HeadObject,
   * design doc "Finalize trusts S3, not the client"). `contentType` is used by
   * `beginUpload` only, to pin the exact-match POST-policy condition. Loosely
   * typed here (like `agentName`) — the BFF's `BeginUploadRequestSchema` /
   * `FinalizeUploadRequestSchema` (Phase F) is the real allowlist gate; the
   * agent re-checks `contentType` against `UPLOAD_TYPES` as defense-in-depth.
   */
  filename: z.string().min(1).max(255).optional(),
  contentType: z.string().min(1).max(255).optional(),
});
export type AgentInvokeRequest = z.infer<typeof AgentInvokeRequestSchema>;

/**
 * Lifecycle of an async turn (ADR-0037): claimed `running` on delivery,
 * resolved `complete`/`failed` by the agent; a `pollTurn` reads it back.
 */
export const TurnStatusSchema = z.enum(['running', 'complete', 'failed']);
export type TurnStatus = z.infer<typeof TurnStatusSchema>;

/**
 * Per-model token counts for one turn (ADR-0063 D13-01/D13-02) — the typed
 * mirror of the Python `ModelUsage` in `contracts/pipeline.py`. Field names
 * are asserted equal on both sides by a shape-parity test (per-side hardcoded
 * fixture, the established convention — see `artifacts.test.ts`).
 *
 * `inputTokens` is the Bedrock provider's CACHE-INCLUSIVE total — pydantic-ai
 * folds cache reads/writes into it before the agent ever sees it — NOT the
 * non-cached remainder Converse's own raw field reports. Price
 * {@link uncachedInputTokens}, never `inputTokens` directly, or every cached
 * token is charged twice (D13-03). A plain `z.object` (not `strictObject`)
 * so the wire's `uncachedInputTokens` — the Python side's own computed
 * mirror of the same value — is dropped rather than rejected; this schema
 * recomputes it independently instead of trusting the wire copy.
 */
export const ModelUsageSchema = z.object({
  modelCalls: z.number().int().nonnegative().default(0),
  toolCalls: z.number().int().nonnegative().default(0),
  inputTokens: z.number().int().nonnegative().default(0),
  outputTokens: z.number().int().nonnegative().default(0),
  cacheReadTokens: z.number().int().nonnegative().default(0),
  cacheWriteTokens: z.number().int().nonnegative().default(0),
});
export type ModelUsage = z.infer<typeof ModelUsageSchema>;

/** Whole-turn totals plus the per-model split (ADR-0063) — mirrors Python `TurnUsage`. */
export const TurnUsageSchema = ModelUsageSchema.extend({
  byModel: z.record(z.string(), ModelUsageSchema).default({}),
});
export type TurnUsage = z.infer<typeof TurnUsageSchema>;

/**
 * Input tokens billed at the FULL input rate. Computed independently on
 * *both* runtimes (mirrors `ModelUsage.uncached_input_tokens` in
 * `contracts/pipeline.py`) rather than trusted off the wire, so D13-03/04
 * cannot silently diverge from the agent's own arithmetic. Clamped at zero:
 * the subtraction is only as trustworthy as the provider's counts, and a
 * negative value would poison a downstream aggregate more quietly than a
 * zero does.
 */
export function uncachedInputTokens(usage: ModelUsage): number {
  return Math.max(0, usage.inputTokens - usage.cacheReadTokens - usage.cacheWriteTokens);
}

/**
 * The reply the Python function returns. **Tolerant-out** (`z.looseObject`): the
 * agent module may add fields and the BFF parse must survive them (expand-contract,
 * ADR-0029). Base fields are `ok`/`module`/`version`/`requestId`; pipeline fields
 * (`reply`, `consensus`) are optional extras the Librarian adds.
 */
export const AgentInvokeResponseSchema = z.looseObject({
  ok: z.boolean(),
  module: z.string(),
  version: z.string(),
  requestId: z.string(),
  echo: z.unknown().optional(),
  // Librarian pipeline extras — tolerant-out, BFF reads but never requires them
  reply: z.unknown().optional(),
  consensus: z.string().optional(),
  // Per-agent envelopes in execution order; the BFF narrows to `agent_name`s.
  agents: z.unknown().optional(),
  // Tracker detail when consensus is `conflicted` (plan flow step 8b).
  conflictDetail: z.string().nullable().optional(),
  // Deliverables written this turn (ADR-0040); the BFF narrows each entry
  // against TurnArtifactSchema, dropping malformed ones.
  artifacts: z.unknown().optional(),
  // Long-lived workstream state; the BFF narrows `mode` out of it (kept
  // z.unknown so a Python-side state-shape change never fails the parse).
  workstreamState: z.unknown().optional(),
  // ADR-0037: present on a `pollTurn` reply — `complete` carries the stored
  // pipeline fields above (reply/agents/consensus/…) alongside it.
  turnStatus: TurnStatusSchema.optional(),
  // ADR-0042: progress checkpoints on a `pollTurn` reply; the BFF narrows
  // each entry against TurnEventSchema, dropping malformed ones.
  events: z.unknown().optional(),
  // ADR-0063 (D13-01 emits, D13-02 narrows): what this turn cost in tokens.
  // z.unknown() like every other Librarian extra above — the BFF narrows via
  // TurnUsageSchema.safeParse; absent or malformed is a no-op, never a
  // rejected reply, because a turn must never fail on telemetry.
  usage: z.unknown().optional(),
});
export type AgentInvokeResponse = z.infer<typeof AgentInvokeResponseSchema>;

/* ── HTTP ping route — POST /api/v1/workstreams/agent/ping ────────────────── */

/**
 * Request body for the diagnostic ping route that exercises the BFF→Python seam.
 * `message` is optional (the BFF defaults it); strict so a stray key is rejected
 * at the edge (the same discipline as the create-flow requests).
 */
export const AgentPingRequestSchema = z.strictObject({
  message: z.string().min(1).max(MAX_MESSAGE_LEN).optional(),
});
export type AgentPingRequest = z.infer<typeof AgentPingRequestSchema>;

/**
 * The `data` payload of the ping response — the agent's (tolerant-out) reply.
 * `data` itself stays strict (fixed shape; the tolerance is on the nested
 * `agent` per the response contract above), wrapped in the standard `{ data, meta }`
 * envelope by the handler.
 */
export const AgentPingDataSchema = z.strictObject({
  agent: AgentInvokeResponseSchema,
});
export type AgentPingData = z.infer<typeof AgentPingDataSchema>;
